"use client";

import React from "react";
import { Alert, AlertDescription, Button } from "@wtc/ui";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";
import { useGoogleDriveStatus } from "@/lib/hooks/google-drive/use-google-drive-status";
import { useGoogleDriveReauth } from "@/lib/hooks/google-drive/use-google-drive-reauth";

interface GoogleDriveAccessBannerProps {
  isSecondAccount?: boolean;
}

/**
 * Proactive degraded-access banner. Surfaces a reconnect prompt the moment the
 * account's Drive scope is missing or needs re-auth — BEFORE the user hits a
 * wall mid-browse. Renders nothing when access is healthy. See the
 * ERROR_STATES_ACTIONABLE rule: warn early, and offer the fix in place.
 */
export function GoogleDriveAccessBanner({ isSecondAccount = false }: GoogleDriveAccessBannerProps) {
  const { data: status } = useGoogleDriveStatus();
  const { isReauthing, startReauth } = useGoogleDriveReauth({ isSecondAccount });

  const account = isSecondAccount ? status?.secondary : status?.primary;

  // Only warn when the account is connected but Drive access is degraded.
  // A fully-disconnected/no-access account is handled by the empty state.
  const degraded =
    !!account?.connected &&
    (account.needsReauth === true ||
      account.driveScopeGranted === false ||
      (account.hasDriveAccess === false && !account.error));

  if (!degraded) return null;

  return (
    <Alert className="mb-3 border-amber-500/50">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span>
          Google Drive access for{" "}
          <span className="font-medium">{account?.email || "this account"}</span> needs to be
          reconnected. Reconnect to restore full access before it interrupts your work.
        </span>
        <Button
          onClick={startReauth}
          disabled={isReauthing}
          variant="outline"
          size="sm"
          className="shrink-0"
        >
          {isReauthing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Redirecting…
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reconnect
            </>
          )}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
